import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Publish as PostIcon,
  OpenInNew as OpenInNewIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingDown as DepreciationIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { fixedAssetsService } from '../../services/fixedAssets.service';
import {
  FixedAsset,
  DepreciationScheduleLine,
  ScheduleStatus,
} from '../../types/fixedAssets.types';

// ─── Translations ─────────────────────────────────────────────────────────────
const translations = {
  en: {
    title: 'Depreciation Schedule',
    back: 'Back',
    assetInfo: 'Asset Information',
    assetCode: 'Asset Code',
    assetName: 'Asset Name',
    acquisitionCost: 'Acquisition Cost',
    residualValue: 'Residual Value',
    method: 'Method',
    usefulLife: 'Useful Life',
    currentBookValue: 'Current Book Value',
    years: 'years',
    straightLine: 'Straight-Line',
    decliningBalance: 'Declining Balance',
    statsTotal: 'Total Periods',
    statsPosted: 'Posted',
    statsPending: 'Pending',
    statsTotalDep: 'Total Depreciation',
    colPeriod: 'Period',
    colYearMonth: 'Year / Month',
    colOpening: 'Opening Value',
    colAmount: 'Depreciation',
    colAccumulated: 'Accumulated',
    colClosing: 'Closing Value',
    colStatus: 'Status',
    colJournal: 'Journal Entry',
    colActions: 'Actions',
    post: 'Post',
    statusPending: 'Pending',
    statusPosted: 'Posted',
    statusSkipped: 'Skipped',
    noSchedule: 'No depreciation schedule found for this asset.',
    loadingAsset: 'Loading asset details...',
    loadingSchedule: 'Loading schedule...',
    errorLoad: 'Failed to load data. Please try again.',
    successPost: 'Period posted successfully.',
    errorPost: 'Failed to post period.',
    kwd: 'KWD',
  },
  ar: {
    title: 'جدول الإهلاك',
    back: 'رجوع',
    assetInfo: 'معلومات الأصل',
    assetCode: 'كود الأصل',
    assetName: 'اسم الأصل',
    acquisitionCost: 'تكلفة الاقتناء',
    residualValue: 'القيمة المتبقية',
    method: 'طريقة الإهلاك',
    usefulLife: 'العمر الإنتاجي',
    currentBookValue: 'القيمة الدفترية الحالية',
    years: 'سنة',
    straightLine: 'القسط الثابت',
    decliningBalance: 'القسط المتناقص',
    statsTotal: 'إجمالي الفترات',
    statsPosted: 'مرحّل',
    statsPending: 'معلق',
    statsTotalDep: 'إجمالي الإهلاك',
    colPeriod: 'الفترة',
    colYearMonth: 'السنة / الشهر',
    colOpening: 'القيمة الافتتاحية',
    colAmount: 'قسط الإهلاك',
    colAccumulated: 'الإهلاك المتراكم',
    colClosing: 'القيمة الختامية',
    colStatus: 'الحالة',
    colJournal: 'قيد اليومية',
    colActions: 'الإجراءات',
    post: 'ترحيل',
    statusPending: 'معلق',
    statusPosted: 'مرحّل',
    statusSkipped: 'متخطى',
    noSchedule: 'لا يوجد جدول إهلاك لهذا الأصل.',
    loadingAsset: 'جاري تحميل تفاصيل الأصل...',
    loadingSchedule: 'جاري تحميل الجدول...',
    errorLoad: 'فشل تحميل البيانات. يرجى المحاولة مرة أخرى.',
    successPost: 'تم ترحيل الفترة بنجاح.',
    errorPost: 'فشل ترحيل الفترة.',
    kwd: 'د.ك',
  },
};

// ─── Month Name Formatter ─────────────────────────────────────────────────────
const MONTHS_EN = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

function formatYearMonth(year: number, month: number, lang: 'en' | 'ar'): string {
  const monthName = lang === 'ar' ? MONTHS_AR[month - 1] : MONTHS_EN[month - 1];
  return `${monthName} ${year}`;
}

function fmt(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

// ─── Status Chip ──────────────────────────────────────────────────────────────
function StatusChip({ status, t }: { status: ScheduleStatus; t: typeof translations['en'] }) {
  const config: Record<ScheduleStatus, { label: string; color: 'warning' | 'success' | 'default' }> = {
    Pending: { label: t.statusPending, color: 'warning' },
    Posted:  { label: t.statusPosted,  color: 'success' },
    Skipped: { label: t.statusSkipped, color: 'default' },
  };
  const { label, color } = config[status];
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{ fontWeight: 'bold', minWidth: 76 }}
    />
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string | number;
  gradient: string;
  icon: React.ReactNode;
}

function StatCard({ label, value, gradient, icon }: StatCardProps) {
  return (
    <Card
      elevation={4}
      sx={{
        background: gradient,
        color: 'white',
        flex: '1 1 200px',
        minWidth: 160,
        transition: 'transform 0.25s',
        '&:hover': { transform: 'translateY(-4px)' },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">{value}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>{label}</Typography>
          </Box>
          <Box sx={{ opacity: 0.25, fontSize: 56, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Info Row ─────────────────────────────────────────────────────────────────
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>{value}</Typography>
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
const FixedAssetSchedulePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const assetId = Number(id);

  const [asset, setAsset] = useState<FixedAsset | null>(null);
  const [schedule, setSchedule] = useState<DepreciationScheduleLine[]>([]);
  const [loadingAsset, setLoadingAsset] = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [postingRow, setPostingRow] = useState<number | null>(null); // scheduleId being posted

  // ── Load asset ──
  const loadAsset = useCallback(async () => {
    if (!assetId) return;
    setLoadingAsset(true);
    try {
      const data = await fixedAssetsService.getById(assetId);
      setAsset(data);
    } catch {
      setError(t.errorLoad);
    } finally {
      setLoadingAsset(false);
    }
  }, [assetId, t.errorLoad]);

  // ── Load schedule ──
  const loadSchedule = useCallback(async () => {
    if (!assetId) return;
    setLoadingSchedule(true);
    try {
      const data = await fixedAssetsService.getSchedule(assetId);
      setSchedule(data);
    } catch {
      setError(t.errorLoad);
    } finally {
      setLoadingSchedule(false);
    }
  }, [assetId, t.errorLoad]);

  useEffect(() => {
    loadAsset();
    loadSchedule();
  }, [loadAsset, loadSchedule]);

  // ── Post a single period ──
  const handlePostPeriod = async (line: DepreciationScheduleLine) => {
    setPostingRow(line.scheduleId);
    try {
      await fixedAssetsService.postPeriod({
        fixedAssetId: assetId,
        scheduleId: line.scheduleId,
      });
      toast.success(t.successPost);
      await loadSchedule();
      await loadAsset(); // refresh current book value
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t.errorPost);
    } finally {
      setPostingRow(null);
    }
  };

  // ── Derived stats ──
  const totalPeriods = schedule.length;
  const postedCount = schedule.filter(s => s.status === 'Posted').length;
  const pendingCount = schedule.filter(s => s.status === 'Pending').length;
  const totalDepreciation = schedule
    .filter(s => s.status === 'Posted')
    .reduce((sum, s) => sum + s.depreciationAmount, 0);

  const methodLabel = asset?.depreciationMethod === 'StraightLine'
    ? t.straightLine
    : t.decliningBalance;

  const isLoading = loadingAsset || loadingSchedule;

  // ── Render ──
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── Page Header ── */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DepreciationIcon sx={{ fontSize: 40, opacity: 0.85 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
              {asset && (
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
                  {asset.assetCode} — {isRTL ? asset.assetNameAr : asset.assetName}
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={isRTL ? undefined : <ArrowBackIcon />}
            endIcon={isRTL ? <ArrowBackIcon sx={{ transform: 'scaleX(-1)' }} /> : undefined}
            onClick={() => navigate(-1)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.6)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            {t.back}
          </Button>
        </Box>
      </Paper>

      {/* ── Asset Info Box ── */}
      {asset && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccountBalanceIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" color="#667eea">
              {t.assetInfo}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 3,
            }}
          >
            <InfoRow label={t.assetCode} value={<Typography component="span" fontWeight="bold" color="#667eea">{asset.assetCode}</Typography>} />
            <InfoRow label={t.assetName} value={isRTL ? asset.assetNameAr : asset.assetName} />
            <InfoRow label={t.acquisitionCost} value={`${fmt(asset.acquisitionCost)} ${t.kwd}`} />
            <InfoRow label={t.residualValue} value={`${fmt(asset.residualValue)} ${t.kwd}`} />
            <InfoRow label={t.method} value={methodLabel} />
            <InfoRow label={t.usefulLife} value={`${asset.usefulLifeYears} ${t.years}`} />
            <InfoRow
              label={t.currentBookValue}
              value={
                <Typography component="span" fontWeight="bold" color="primary.main">
                  {`${fmt(asset.currentBookValue)} ${t.kwd}`}
                </Typography>
              }
            />
          </Box>
        </Paper>
      )}

      {/* ── Stats Row ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          label={t.statsTotal}
          value={totalPeriods}
          gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          icon={<ScheduleIcon sx={{ fontSize: 56 }} />}
        />
        <StatCard
          label={t.statsPosted}
          value={postedCount}
          gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
          icon={<CheckCircleIcon sx={{ fontSize: 56 }} />}
        />
        <StatCard
          label={t.statsPending}
          value={pendingCount}
          gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
          icon={<PendingIcon sx={{ fontSize: 56 }} />}
        />
        <StatCard
          label={t.statsTotalDep}
          value={`${fmt(totalDepreciation)} ${t.kwd}`}
          gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
          icon={<DepreciationIcon sx={{ fontSize: 56 }} />}
        />
      </Box>

      {/* ── Schedule Table ── */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {schedule.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <CalendarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">{t.noSchedule}</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                {[
                  t.colPeriod,
                  t.colYearMonth,
                  t.colOpening,
                  t.colAmount,
                  t.colAccumulated,
                  t.colClosing,
                  t.colStatus,
                  t.colJournal,
                  t.colActions,
                ].map((col) => (
                  <TableCell key={col} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {schedule.map((line) => {
                const isPosting = postingRow === line.scheduleId;
                const isPosted = line.status === 'Posted';
                const isPending = line.status === 'Pending';

                return (
                  <TableRow
                    key={line.scheduleId}
                    hover
                    sx={{
                      '&:hover': { bgcolor: 'rgba(102,126,234,0.04)' },
                      opacity: line.status === 'Skipped' ? 0.55 : 1,
                    }}
                  >
                    {/* Period # */}
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="#667eea">
                        #{line.periodNumber}
                      </Typography>
                    </TableCell>

                    {/* Year / Month */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {formatYearMonth(line.year, line.month, language)}
                      </Typography>
                    </TableCell>

                    {/* Opening Book Value */}
                    <TableCell align="right">
                      <Typography variant="body2">{fmt(line.openingBookValue)}</Typography>
                    </TableCell>

                    {/* Depreciation Amount */}
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500} color="error.main">
                        {fmt(line.depreciationAmount)}
                      </Typography>
                    </TableCell>

                    {/* Accumulated Depreciation */}
                    <TableCell align="right">
                      <Typography variant="body2">{fmt(line.accumulatedDepreciation)}</Typography>
                    </TableCell>

                    {/* Closing Book Value */}
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {fmt(line.closingBookValue)}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <StatusChip status={line.status} t={t} />
                    </TableCell>

                    {/* Journal Entry # */}
                    <TableCell>
                      {isPosted && line.journalEntryNumber ? (
                        <Tooltip title={line.journalEntryNumber}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: '#667eea',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {line.journalEntryNumber}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      {isPending ? (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={isPosting ? <CircularProgress size={14} color="inherit" /> : <PostIcon />}
                          disabled={isPosting}
                          onClick={() => handlePostPeriod(line)}
                          sx={{
                            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                            boxShadow: '0 2px 6px rgba(102,126,234,.35)',
                            fontSize: '0.75rem',
                            py: 0.5,
                            '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' },
                            '&:disabled': { background: 'rgba(0,0,0,0.12)' },
                          }}
                        >
                          {t.post}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default FixedAssetSchedulePage;
